import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Storage as ChunkIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Speed as SpeedIcon,
  Psychology as PsychologyIcon,
  TrendingUp as ScoreIcon,
  Description as DocumentIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { ragService } from '../services/ragService';
import RetrievalMethodExplanation from './common/RetrievalMethodExplanation';

const ChunkTester = ({ 
  kbId,
  knowledgeBase,
  disabled = false,
  onClose,
  onApplyConfig = null  // Callback para aplicar configurações testadas de volta à KB
}) => {
  const [inputQuery, setInputQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const messagesEndRef = useRef(null);

  // Configurações de busca
  const [searchConfig, setSearchConfig] = useState({
    method: 'semantic',
    top_k: 5,
    threshold: 0.5,  // Reduzido de 0.7 para 0.5 para mostrar mais resultados relevantes
    semantic_weight: 0.6,
    bm25_weight: 0.4,
    mmr_lambda: 0.5,
    query_variations: 3,
    rerank_enabled: false,
    rerank_top_n: 10,
  });

  // Estado para mostrar/esconder configurações
  const [configExpanded, setConfigExpanded] = useState(false);
  const [debugExpanded, setDebugExpanded] = useState(false);

  // Scroll automático para última pesquisa
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [searchHistory]);

  // Sincronizar configurações com a KB quando carregada
  useEffect(() => {
    if (knowledgeBase) {
      setSearchConfig(prev => ({
        ...prev,
        method: knowledgeBase.retrieval_method || 'semantic',
        top_k: knowledgeBase.retrieval_config?.top_k || 5,
        threshold: knowledgeBase.retrieval_config?.threshold || 0.5,
        semantic_weight: knowledgeBase.retrieval_config?.semantic_weight || 0.6,
        bm25_weight: knowledgeBase.retrieval_config?.bm25_weight || 0.4,
        rerank_enabled: knowledgeBase.retrieval_config?.rerank_enabled || false,
        rerank_top_n: knowledgeBase.retrieval_config?.rerank_top_n || 10,
      }));
    }
  }, [knowledgeBase]);

  const handleSearch = async () => {
    if (!inputQuery.trim() || searching || disabled) return;

    const query = inputQuery.trim();
    setInputQuery('');
    setSearching(true);
    setError(null);

    // Capturar timestamp de início
    const startTime = Date.now();

    // Criar entrada do histórico fora do try/catch para ser acessível em ambos os blocos
    const searchEntry = {
      id: Date.now(),
      query,
      timestamp: new Date(),
      status: 'searching'
    };

    setSearchHistory(prev => [...prev, searchEntry]);

    // Reset debug accordion estado para nova busca
    setDebugExpanded(false);

    // Preparar informações de debug iniciais
      const searchParams = {
        query,
        method: searchConfig.method,
        top_k: searchConfig.top_k,
        threshold: searchConfig.threshold,
        semantic_weight: searchConfig.semantic_weight,
        bm25_weight: searchConfig.bm25_weight,
        mmr_lambda: searchConfig.mmr_lambda,
        query_variations: searchConfig.query_variations,
        rerank_enabled: searchConfig.rerank_enabled,
        rerank_top_n: searchConfig.rerank_top_n,
      };

    // Atualizar debug info com parâmetros da busca
    setDebugInfo({
      status: 'searching',
      startTime: new Date(startTime),
      parameters: searchParams,
      knowledgeBase: {
        id: kbId,
        name: knowledgeBase?.name || 'Unknown',
        embedding_model: knowledgeBase?.embedding_model || 'Unknown'
      }
    });

    try {
      const results = await ragService.searchKnowledgeBase(kbId, searchParams);

      // Capturar tempo de resposta
      const endTime = Date.now();
      const responseTime = endTime - startTime;

      // Atualizar debug info com resultados
      setDebugInfo(prev => ({
        ...prev,
        status: 'completed',
        endTime: new Date(endTime),
        responseTime,
        results: {
          totalFound: results.total_found || 0,
          searchTime: results.search_time || 0,
          method: results.method || searchConfig.method,
          knowledgeBaseName: results.knowledge_base || knowledgeBase?.name
        }
      }));

      // Atualizar entrada no histórico com resultados
      setSearchHistory(prev => 
        prev.map(entry => 
          entry.id === searchEntry.id 
            ? { ...entry, status: 'completed', results: results.results || [] }
            : entry
        )
      );

      setSearchResults(results.results || []);

    } catch (err) {
      console.error('Erro na busca:', err);
      setError('Erro ao realizar busca: ' + err.message);
      
      // Atualizar debug info com erro
      const endTime = Date.now();
      setDebugInfo(prev => ({
        ...prev,
        status: 'error',
        endTime: new Date(endTime),
        responseTime: endTime - startTime,
        error: err.message
      }));
      
      // Atualizar entrada no histórico com erro
      setSearchHistory(prev => 
        prev.map(entry => 
          entry.id === searchEntry.id 
            ? { ...entry, status: 'error', error: err.message }
            : entry
        )
      );
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const clearHistory = () => {
    setSearchHistory([]);
    setSearchResults([]);
    setError(null);
  };

  // Aplicar configurações testadas de volta à KB
  const applyConfigToKB = () => {
    if (onApplyConfig) {
      onApplyConfig({
        retrieval_method: searchConfig.method,
        retrieval_config: {
          top_k: searchConfig.top_k,
          threshold: searchConfig.threshold,
          semantic_weight: searchConfig.semantic_weight,
          bm25_weight: searchConfig.bm25_weight,
          rerank_enabled: searchConfig.rerank_enabled,
          rerank_top_n: searchConfig.rerank_top_n,
        }
      });
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning'; 
    return 'error';
  };

  const suggestedQueries = [
    "Como configurar integração?",
    "Informações sobre preços",
    "Funcionalidades principais", 
    "Como fazer upload de documentos?",
  ];

  return (
    <Box 
      sx={{ 
        height: '100%', 
        width: '100%',
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0,
        maxWidth: 'none', // Remove limitação de largura
      }}
    >
      {/* Configurações de Busca */}
      <Accordion 
        expanded={configExpanded} 
        onChange={() => setConfigExpanded(!configExpanded)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon sx={{ fontSize: 20 }} />
            <Typography variant="subtitle2">
              Configurações de Busca
            </Typography>
            <Chip 
              label={searchConfig.method} 
              size="small" 
              variant="outlined"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            {/* Seção: Configurações Básicas */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                Configurações Básicas
              </Typography>
              <Grid container spacing={2}>
                {/* Método de busca com tooltip */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Método de Busca</InputLabel>
                      <Select
                        value={searchConfig.method}
                        onChange={(e) => setSearchConfig(prev => ({ ...prev, method: e.target.value }))}
                        label="Método de Busca"
                      >
                        <MenuItem value="semantic">
                          <Typography variant="body2">Semântica</Typography>
                        </MenuItem>
                        <MenuItem value="hybrid">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">Híbrida</Typography>
                            <Chip label="Recomendada" size="small" color="success" />
                          </Box>
                        </MenuItem>
                        <MenuItem value="lexical">
                          <Typography variant="body2">Lexical (BM25)</Typography>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <RetrievalMethodExplanation tooltipOnly />
                  </Box>
                </Grid>

                {/* Top K */}
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Top K"
                    type="number"
                    size="small"
                    value={searchConfig.top_k}
                    onChange={(e) => setSearchConfig(prev => ({ ...prev, top_k: parseInt(e.target.value) }))}
                    inputProps={{ min: 1, max: 20 }}
                    helperText="Máx. resultados"
                    fullWidth
                  />
                </Grid>

                {/* Threshold com tooltip */}
                <Grid item xs={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Threshold"
                      type="number"
                      size="small"
                      value={searchConfig.threshold}
                      onChange={(e) => setSearchConfig(prev => ({ ...prev, threshold: parseFloat(e.target.value) }))}
                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                      helperText="Score mínimo (0-1)"
                      fullWidth
                    />
                    <Tooltip 
                      title="Score mínimo (0-1) para considerar um resultado relevante. Quanto maior, mais restritivo." 
                      placement="top"
                    >
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Seção: Configurações do Método Híbrido */}
            {searchConfig.method === 'hybrid' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'success.main' }}>
                  Configurações Híbridas
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
                      Peso Semântica: {searchConfig.semantic_weight.toFixed(1)}
                    </Typography>
                    <Slider
                      value={searchConfig.semantic_weight}
                      onChange={(e, value) => setSearchConfig(prev => ({ 
                        ...prev, 
                        semantic_weight: value,
                        bm25_weight: 1 - value 
                      }))}
                      min={0}
                      max={1}
                      step={0.1}
                      size="small"
                      marks={[
                        { value: 0, label: '0' },
                        { value: 0.5, label: '0.5' },
                        { value: 1, label: '1' }
                      ]}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Prioriza busca por significado e conceitos
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
                      Peso BM25: {searchConfig.bm25_weight.toFixed(1)}
                    </Typography>
                    <Slider
                      value={searchConfig.bm25_weight}
                      onChange={(e, value) => setSearchConfig(prev => ({ 
                        ...prev, 
                        bm25_weight: value,
                        semantic_weight: 1 - value 
                      }))}
                      min={0}
                      max={1}
                      step={0.1}
                      size="small"
                      marks={[
                        { value: 0, label: '0' },
                        { value: 0.5, label: '0.5' },
                        { value: 1, label: '1' }
                      ]}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Prioriza busca por palavras-chave exatas
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Seção: Configurações Avançadas */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'info.main' }}>
                Configurações Avançadas
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={searchConfig.rerank_enabled}
                    onChange={(e) => setSearchConfig(prev => ({ ...prev, rerank_enabled: e.target.checked }))}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      Habilitar Re-ranking
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Melhora a qualidade dos resultados com processamento adicional
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Botões de Ação */}
            <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={applyConfigToKB}
                  disabled={!onApplyConfig}
                >
                  Aplicar às Configurações
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={clearHistory}
                  disabled={searchHistory.length === 0}
                >
                  Limpar Histórico
                </Button>
              </Stack>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Área de Histórico de Buscas */}
      <Box 
        sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2,
          width: '100%',
          maxWidth: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Estado inicial */}
        {searchHistory.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Avatar 
              sx={{ 
                width: 64, 
                height: 64, 
                mx: 'auto', 
                mb: 2,
                bgcolor: 'info.main',
              }}
            >
              <SearchIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Teste sua Base de Conhecimento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Digite uma pergunta para ver quais chunks serão retornados e seus scores de relevância
            </Typography>

            {/* Queries sugeridas */}
            {!disabled && knowledgeBase && (
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Perguntas sugeridas:
                </Typography>
                {suggestedQueries.map((query, index) => (
                  <Chip
                    key={index}
                    label={query}
                    variant="outlined"
                    size="small"
                    onClick={() => setInputQuery(query)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { 
                        backgroundColor: 'action.hover' 
                      },
                    }}
                  />
                ))}
              </Stack>
            )}

            {disabled && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Base de conhecimento não disponível para teste
              </Alert>
            )}
          </Box>
        )}

        {/* Histórico de Buscas */}
        <AnimatePresence>
          {searchHistory.map((search) => (
            <motion.div
              key={search.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Query do usuário */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  flexDirection: 'row-reverse',
                  mb: 1,
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: 'primary.main',
                    width: 36,
                    height: 36,
                  }}
                >
                  <PersonIcon />
                </Avatar>

                <Box sx={{ flex: 1, maxWidth: '80%' }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {search.query}
                    </Typography>
                  </Paper>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
                  >
                    {formatTime(search.timestamp)}
                  </Typography>
                </Box>
              </Box>

              {/* Resultados da busca */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  mb: 3,
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: 'info.main',
                    width: 36,
                    height: 36,
                  }}
                >
                  <SearchIcon />
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  {search.status === 'searching' && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Buscando chunks relevantes...
                      </Typography>
                    </Paper>
                  )}

                  {search.status === 'error' && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {search.error}
                    </Alert>
                  )}

                                    {search.status === 'completed' && search.results && (
                    <Box>
                      {/* Debug Info Accordion - Compact Summary */}
                      {debugInfo && (
                        <Accordion 
                          expanded={debugExpanded}
                          onChange={() => setDebugExpanded(!debugExpanded)}
                          sx={{ 
                            mb: 2, 
                            '&:before': { display: 'none' },
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '8px !important',
                          }}
                        >
                          <AccordionSummary 
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              bgcolor: 'rgba(25, 118, 210, 0.04)',
                              borderRadius: '8px',
                              '&.Mui-expanded': {
                                borderRadius: '8px 8px 0 0',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Badge 
                                  badgeContent={debugInfo.results?.totalFound || 0} 
                                  color={debugInfo.results?.totalFound > 0 ? "primary" : "error"}
                                >
                                  <SearchIcon sx={{ fontSize: 20 }} />
                                </Badge>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Debug Info
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
                                <Chip 
                                  label={`${debugInfo.parameters?.method || 'N/A'}`}
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                />
                                <Chip 
                                  label={`${debugInfo.responseTime}ms`}
                                  size="small" 
                                  color={debugInfo.responseTime > 2000 ? "warning" : debugInfo.responseTime > 5000 ? "error" : "success"}
                                  variant="outlined"
                                />
                                <Chip 
                                  label={`Top-${debugInfo.parameters?.top_k || 'N/A'}`}
                                  size="small" 
                                  variant="outlined"
                                />
                                <Chip 
                                  label={`T≥${debugInfo.parameters?.threshold || 'N/A'}`}
                                  size="small" 
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          </AccordionSummary>
                          
                          <AccordionDetails sx={{ pt: 2 }}>
                            <Grid container spacing={3}>
                              {/* Parâmetros */}
                              <Grid item xs={12} sm={6}>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.02)' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                                    🔧 Parâmetros
                                  </Typography>
                                  <Stack spacing={0.5}>
                                    <Typography variant="caption">Método: <strong>{debugInfo.parameters?.method}</strong></Typography>
                                    <Typography variant="caption">Top K: <strong>{debugInfo.parameters?.top_k}</strong></Typography>
                                    <Typography variant="caption">Threshold: <strong>{debugInfo.parameters?.threshold}</strong></Typography>
                                    {debugInfo.parameters?.method === 'hybrid' && (
                                      <>
                                        <Typography variant="caption">Peso Semântica: <strong>{debugInfo.parameters?.semantic_weight}</strong></Typography>
                                        <Typography variant="caption">Peso BM25: <strong>{debugInfo.parameters?.bm25_weight}</strong></Typography>
                                      </>
                                    )}
                                  </Stack>
                                </Paper>
                              </Grid>

                              {/* Knowledge Base */}
                              <Grid item xs={12} sm={6}>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.02)' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'secondary.main' }}>
                                    🗃️ Knowledge Base
                                  </Typography>
                                  <Stack spacing={0.5}>
                                    <Typography variant="caption">Nome: <strong>{debugInfo.knowledgeBase?.name}</strong></Typography>
                                    <Typography variant="caption">Modelo: <strong>{debugInfo.knowledgeBase?.embedding_model}</strong></Typography>
                                    <Typography variant="caption">ID: <strong>{debugInfo.knowledgeBase?.id?.slice(0, 8)}...</strong></Typography>
                                  </Stack>
                                </Paper>
                              </Grid>

                              {/* Resultados */}
                              {debugInfo.results && (
                                <Grid item xs={12} sm={6}>
                                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.02)' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'success.main' }}>
                                      📊 Resultados
                                    </Typography>
                                    <Stack spacing={0.5}>
                                      <Typography variant="caption">Total Encontrado: <strong>{debugInfo.results.totalFound}</strong></Typography>
                                      <Typography variant="caption">Tempo Backend: <strong>{(debugInfo.results.searchTime * 1000).toFixed(0)}ms</strong></Typography>
                                      <Typography variant="caption">Tempo Total: <strong>{debugInfo.responseTime}ms</strong></Typography>
                                      <Typography variant="caption">Método Usado: <strong>{debugInfo.results.method}</strong></Typography>
                                    </Stack>
                                  </Paper>
                                </Grid>
                              )}

                              {/* Timeline */}
                              <Grid item xs={12} sm={6}>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(158, 158, 158, 0.02)' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                                    ⏰ Timeline
                                  </Typography>
                                  <Stack spacing={0.5}>
                                    <Typography variant="caption">Início: <strong>{debugInfo.startTime?.toLocaleTimeString()}</strong></Typography>
                                    {debugInfo.endTime && (
                                      <Typography variant="caption">Fim: <strong>{debugInfo.endTime.toLocaleTimeString()}</strong></Typography>
                                    )}
                                  </Stack>
                                </Paper>
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Chunks Results - Main Content */}
                      <Box>
                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          📊 Encontrados {search.results.length} chunks relevantes:
                        </Typography>
                        
                        <Stack spacing={2}>
                          {search.results.map((chunk, index) => (
                            <Card 
                              key={chunk.id || index} 
                              variant="outlined" 
                              sx={{ 
                                borderRadius: 2,
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              <CardContent sx={{ p: 2 }}>
                                {/* Header do chunk */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DocumentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {chunk.source?.document || 'Documento'}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    icon={<ScoreIcon sx={{ fontSize: 16 }} />}
                                    label={`${(chunk.score * 100).toFixed(1)}%`}
                                    size="small"
                                    color={getScoreColor(chunk.score)}
                                    variant="outlined"
                                  />
                                </Box>
                                
                                {/* Conteúdo do chunk */}
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    lineHeight: 1.5,
                                    color: 'text.primary',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 4,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {chunk.content}
                                </Typography>
                                
                                {/* Metadados */}
                                <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Chunk {chunk.source?.chunk_index || index} • {chunk.content?.length || 0} caracteres
                                    {chunk.explanation && ` • ${chunk.explanation}`}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>

                        {search.results.length === 0 && (
                          <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            Nenhum chunk encontrado para esta consulta. Tente ajustar os parâmetros de busca.
                          </Alert>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Indicador de busca em andamento */}
        {searching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: 'info.main',
                  width: 36,
                  height: 36,
                }}
              >
                <SearchIcon />
              </Avatar>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Buscando nos {knowledgeBase?.stats?.total_chunks || 'seus'} chunks...
                </Typography>
              </Paper>
            </Box>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input de Query */}
      <Box 
        sx={{ 
          p: 2, 
          borderTop: '1px solid', 
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        {/* Informações da sessão */}
        {searchHistory.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 1,
            p: 1,
            bgcolor: 'background.default',
            borderRadius: 1,
          }}>
            <Typography variant="caption" color="text.secondary">
              🔍 {searchHistory.length} busca{searchHistory.length > 1 ? 's' : ''} realizada{searchHistory.length > 1 ? 's' : ''}
            </Typography>
            <Button
              size="small"
              onClick={clearHistory}
              sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
            >
              Limpar
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder={
              disabled ? 'Base de conhecimento não disponível' : 
              'Digite sua pergunta para buscar chunks relevantes...'
            }
            value={inputQuery}
            onChange={(e) => setInputQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={disabled || searching}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          
          <IconButton
            color="primary"
            onClick={handleSearch}
            disabled={!inputQuery.trim() || disabled || searching}
            sx={{
              bgcolor: 'info.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'info.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            {searching ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SearchIcon />
            )}
          </IconButton>
        </Box>

        {/* Dicas */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ display: 'block', mt: 1, textAlign: 'center' }}
        >
          {disabled ? 
            'Base de conhecimento não disponível para teste' :
            `Pressione Enter para buscar • ${searchConfig.method} • Top-${searchConfig.top_k} • Threshold: ${searchConfig.threshold}`
          }
        </Typography>
      </Box>
    </Box>
  );
};

export default ChunkTester; 